import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { PaginationParams } from '../../types/pagination'
import { filterDuplicateNumbers } from '../../utils/array'

import { usePagination, PaginationProps } from './usePagination'

export interface GenericEntity {
  id: number
  selected?: boolean
}

export interface CheckboxPaginationProps<T extends GenericEntity> extends PaginationProps {
  entities: T[]
  serverSelectedCount?: number
  serverSelectedOptions?: number[] // ids of selected entities from the server
}

const LIMIT_PER_PAGE = 50

export const useCheckboxPaginationWithInitialIDs = <T extends GenericEntity>({
  entities,
  totalCount,
  serverSelectedOptions,
  handleFirstPage,
  handleLastPage,
  handleNextPage,
  handlePrevPage,
}: CheckboxPaginationProps<T>) => {
  const {
    page,
    sort,
    search,
    debouncedSearch,
    pageCount,
    setSort,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPrevPage,
    searchPage,
    onSearchInputChange,
    setPageCount,
  } = usePagination({ handleFirstPage, handleLastPage, handleNextPage, handlePrevPage, totalCount })
  const { t } = useTranslation('PMPlatforms')
  const [selectedOptions, setSelectedOptions] = useState<number[]>([])
  const [isInitialized, setIsInitialized] = useState(false) // Tracks initialization

  const selectedCount = selectedOptions.length

  const isAllSelected =
    selectedOptions.length > 0 &&
    entities.length > 0 &&
    entities.every((entity) => selectedOptions.includes(entity.id))

  const selectedText = useMemo(() => {
    if (!selectedCount) return undefined
    return search.length > 0
      ? t('table.selected', { selected: selectedCount })
      : t('table.selected-range', {
          selected: selectedCount,
          total: totalCount,
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCount, search, entities])

  const paginationParams: PaginationParams = useMemo(() => {
    const sortParams = sort ? { sort } : {}
    if (debouncedSearch.length) {
      return { limit: LIMIT_PER_PAGE, page, search: debouncedSearch, ...sortParams }
    } else {
      return { limit: LIMIT_PER_PAGE, page, ...sortParams }
    }
  }, [debouncedSearch, page, sort])

  const handleSelectAll = () => {
    if (isAllSelected)
      setSelectedOptions(
        selectedOptions.filter((id) => !entities.map((entity) => entity.id).includes(id))
      )
    else {
      const allIds = entities.map((entity) => entity.id)
      setSelectedOptions((prev) => filterDuplicateNumbers([...prev, ...allIds]))
    }
  }

  // Update selectedOptions only once when serverSelectedOptions is provided
  useEffect(() => {
    if (!isInitialized && serverSelectedOptions) {
      setSelectedOptions(serverSelectedOptions)
      setIsInitialized(true)
    }
  }, [serverSelectedOptions, isInitialized])

  const handleCheckboxClicked = (id: number) => {
    setSelectedOptions((prevState) => {
      const updatedState = prevState.includes(id)
        ? prevState.filter((selectedId) => selectedId !== id) // Deselect
        : [...prevState, id] // Select
      return updatedState
    })
  }

  const cleanSelection = () => setSelectedOptions([])

  return {
    handleCheckboxClicked,
    handleSelectAll,
    selectedOptions,
    setSelectedOptions,
    page,
    sort,
    search,
    debouncedSearch,
    pageCount,
    setSort,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPrevPage,
    searchPage,
    onSearchInputChange,
    setPageCount,
    selectedCount,
    cleanSelection,
    selectedText,
    isAllSelected,
    paginationParams,
  }
}
